import styled from "@emotion/styled";
import {
  Body3RegularTypography,
  Button,
  FlexColumn,
  getColor,
  Heading2SemiboldTypography,
  StyledImage,
} from "@wish-ui";
import { useEffect, useState } from "react";
import { getPlatform } from "utils/config";
import { getAssetUrl } from "utils/common";
import WishlinkLottie from "WishlinkComponents/WishlinkLottieLazyLoad";

const StyledContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  text-align: center;
  flex: 1;
`;

const StyledContent = styled.div`
  position: absolute;
  bottom: 0;
  background: ${getColor("background.default.default")};
  left: 50%;
  right: "0";
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transform: translateX(-50%);
  max-width: 448px;
  width: 100%;
  height: 100vh;
  flex: 1;
`;

const Loader = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;
  border-radius: 9px;
  height: 5px;
  background: ${getColor("background.default.secondary")};
  overflow: hidden;
  :after {
    content: "jj";
    position: absolute;
    width: 30%;
    height: 100%;
    background: ${getColor("background.wishlink.default")};
    border-radius: 4px;
    animation: load 1s linear infinite;
    transform-origin: 0 0;
  }
  @keyframes load {
    0% {
      width: 30%;
      transform: translateX(-300%);
    }
    50% {
      width: 30%;
      transform: translateX(0%);
    }
    100% {
      width: 30%;
      transform: translateX(200%);
    }
  }
`;

export const WebForceUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState("IDLE");

  const handleUpdate = () => {
    setUpdateAvailable("LOADING");
  };

  useEffect(() => {
    if (updateAvailable !== "LOADING") return;
    // Reload onto the freshly served assets, after a beat so the
    // "Installing Update" state is actually visible.
    const timerToken = setTimeout(() => {
      window.location.reload();
    }, 3000);
    return () => clearTimeout(timerToken);
  }, [updateAvailable]);

  if (getPlatform() === "web") return null;

  return (
    <StyledContainer role="dialog" aria-hidden="true" data-noindex="true">
      <StyledContent>
        <FlexColumn
          gap={32}
          alignItems="center"
          justifyContent="center"
          flex={1}
        >
          {updateAvailable === "LOADING" ? (
            <WishlinkLottie
              play={true}
              loop={true}
              path={getAssetUrl("UpdateLoader.lottie")}
              style={{
                width: 140,
                height: 145,
              }}
            />
          ) : (
            <StyledImage
              src={getAssetUrl("UpdateCentral.png")}
              alt="New version available"
              width={140}
              height={145}
            />
          )}
          <FlexColumn
            gap={8}
            alignItems="center"
            justifyContent="center"
            aria-hidden="true"
            data-noindex="true"
          >
            <Heading2SemiboldTypography
              textAlign="center"
              fontFamily="Lexend"
              color="text.default.default"
            >
              {updateAvailable === "LOADING"
                ? "Installing Update"
                : "New Update Available"}
            </Heading2SemiboldTypography>
            <Body3RegularTypography
              textAlign="center"
              color="text.default.secondary"
            >
              {updateAvailable === "LOADING"
                ? "This will only take a few seconds"
                : "Update your Wishlink app to the latest version for a better experience"}
            </Body3RegularTypography>
          </FlexColumn>
        </FlexColumn>

        <FlexColumn
          mt="auto"
          alignItems="center"
          justifyContent="center"
          px={updateAvailable === "IDLE" ? 20 : 32}
          py={updateAvailable === "IDLE" ? 20 : 24}
          gap={updateAvailable === "IDLE" ? 0 : 16}
        >
          {updateAvailable === "LOADING" && (
            <>
              <Body3RegularTypography
                textAlign="center"
                color="text.default.default"
              >
                Keep Wishlink open while we update
              </Body3RegularTypography>
              <Loader />
            </>
          )}
          {updateAvailable === "IDLE" && (
            <Button variant="primary" onClick={handleUpdate}>
              Update
            </Button>
          )}
        </FlexColumn>
      </StyledContent>
    </StyledContainer>
  );
};
